import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigItemDefinition,
} from '@blaze-shared/elements-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { generateScoreFormField } from './generation/form-field';
import { ScoreComponent } from './score.component';
import { type ScorePropsModel, type ScoreValueState, type ScoreValueStyle } from './score.models';
import { ScorePropsBuilder } from './score.props-builder';

export type ScoreElement = CanvasElementFrom<'score'>;
export type ScoreDef = WithProps<ScorePropsModel> & WithIoConfig;

declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    score: ScoreDef;
  }
}

const DEFAULT_WIDTH = 217;
const DEFAULT_HEIGHT = 32;
const MIN_SIZE = 32;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  type: 'score',
  componentType: ScoreComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Score',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: ScorePropsBuilder,
  generation: {
    formField: generateScoreFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  styleExcludePaths: ['style.value.currentState'],
  metaUiFeatures: {
    type: 'input',
    paths: {
      readonly: 'content.options.readonly',
    },
  },
  features: {
    ai: {
      optionalInput: true,
    },
  },
  version: 1,
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Value', dataType: 'Number' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          columns: [
            {
              columnType: 'Number',
              dataType: 'Number',
              isProfilePicture: false,
              name: '',
            },
          ],
          id: '',
          name: 'Score',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'score'> {
  const valueStyle = (
    color: string,
    fill: { color1: string; selectorType: 'solid' | 'none' },
  ): ScoreValueStyle => ({
    color: { color1: color, selectorType: 'solid' },
    fontFamily: '$t.fontFamily',
    fontWeight: '$t.fontWeight' as FontWeightOption,
    fontSize: '$t.fontSize',
    fill,
    outline: { color1: '$t.outline', selectorType: 'none' },
    outlineWidth: '0px',
    radius: '100px',
  });

  return {
    content: {
      title: {
        label: 'Label',
        position: 'left',
        align: 'left',
        width: '35%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      options: {
        min: 1,
        max: 5,
        readonly: false,
      },
    },
    style: {
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      value: {
        currentState: 'default',
        default: valueStyle('$t.value', { color1: '$t.fill', selectorType: 'solid' }),
        hover: valueStyle('$t.value', { color1: '$t.complementary', selectorType: 'solid' }),
        selected: valueStyle('$t.accentLabel', { color1: '$t.accent', selectorType: 'solid' }),
      },
    },
  };
}

function getInitial(): PartialPropsOf<'score'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}

function valueTextFields(path: string): PropertiesConfigItemDefinition[] {
  return [
    epc.fields.colorField(`${path}.color`, 'Value Color', {
      allowGradient: false,
      allowNone: false,
    }),
    ...epc.fields.fontFamilyAndWeightFields(`${path}.fontFamily`, `${path}.fontWeight`),
    epc.fields.sliderField(`${path}.fontSize`, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
  ];
}

function valueBackgroundFields(path: string): PropertiesConfigItemDefinition[] {
  return [
    epc.fields.colorField(`${path}.fill`, 'Fill', {
      allowGradient: false,
      allowNone: true,
    }),
    epc.fields.colorField(`${path}.outline`, 'Outline', {
      allowGradient: false,
    }),
    epc.fields.sliderField(`${path}.outlineWidth`, 'Width', [{ min: 0, max: 10, unit: 'px' }]),
    epc.fields.sliderField(`${path}.radius`, 'Radius', [{ min: 0, max: 100, unit: 'px' }]),
  ];
}

function valueStateFields(path: string): PropertiesConfigItemDefinition[] {
  return [
    epc.utils.separator('Value Text', { hideTopSeparator: true, useNormalFontWeight: true }),
    ...valueTextFields(path),
    epc.utils.separator('Value Background', { useNormalFontWeight: true }),
    ...valueBackgroundFields(path),
  ];
}

function valueStateTabFields(state: ScoreValueState): PropertiesConfigItemDefinition[] {
  return epc.utils.conditionalFields(
    valueStateFields(`style.value.${state}`),
    'style.value.currentState',
    (current: ScoreValueState) => current !== state,
  );
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('score', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.options',
        'Options',
        [
          epc.fields.textInput('content.options.min', 'Min value', { inputType: 'number' }),
          epc.fields.textInput('content.options.max', 'Max value', { inputType: 'number' }),
          epc.fields.textInput('content.options.defaultValue', 'Default value', {
            inputType: 'number',
            placeholder: 'Optional',
          }),
          epc.fields.toggleField('content.options.readonly', 'Read Only'),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'Value',
              sources: 'field',
              bindings: [{ property: 'value', label: 'Value', dataType: 'Number' }],
            },
          ],
        },
      ),

      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),

      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.value', 'Value', [
          epc.fields.radiosField(
            'style.value.currentState',
            'Value',
            [
              { value: 'default', label: 'Default' },
              { value: 'hover', label: 'Hover' },
              { value: 'selected', label: 'Selected' },
            ],
            { hideLabel: true },
          ),
          ...valueStateTabFields('default'),
          ...valueStateTabFields('hover'),
          ...valueStateTabFields('selected'),
        ]),

        epc.subsections.labelStyleSubsection({
          color: 'style.general.title',
          enabled: 'content.title.enabled',
          fontFamily: 'style.labelBlock.fontFamily',
          fontSize: 'style.labelBlock.fontSize',
          fontStyle: 'style.labelBlock.fontStyle',
          fontWeight: 'style.labelBlock.fontWeight',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.labelBlock.iconSize',
          lineHeight: 'style.labelBlock.lineHeight',
          position: 'content.title.position',
          width: 'content.title.width',
        }),
      ],
      true,
    ),
  ]);
}
